'use client';

import { useEffect, useRef, useState } from 'react';
import {
  InteractionApiError,
  consumeMagicLink,
  reportInteractionFailure,
} from '@/app/utils/auth-interactions-api';
import type {
  InteractionApprovedHandler,
  InteractionTerminalHandler,
  TerminalInteractionState,
} from './types';
import TerminalInteractionStateView, { approvedElsewhereState } from './terminal-state';

export default function MagicLinkConsumeState({
  interactionToken,
  magicLinkToken,
  sameDevice,
  onApproved,
  onTerminal,
  onRestart,
}: {
  interactionToken: string;
  magicLinkToken: string;
  sameDevice: boolean;
  onApproved: InteractionApprovedHandler;
  onTerminal: InteractionTerminalHandler;
  onRestart: () => void;
}) {
  // Keep one consume request while still letting Strict Mode's effect replay update state.
  const consumedRef = useRef(false);
  const mountedRef = useRef(false);
  const [terminalState, setTerminalState] = useState<TerminalInteractionState | null>(null);
  const [heading, setHeading] = useState('Checking magic link');
  const [message, setMessage] = useState('Please wait while we finish approving this sign in.');

  useEffect(() => {
    mountedRef.current = true;

    async function consume() {
      if (consumedRef.current) {
        return;
      }
      consumedRef.current = true;

      try {
        const result = await consumeMagicLink({
          interactionToken,
          magicLinkToken,
          sameDevice,
        });
        if (!mountedRef.current) {
          return;
        }
        if (result.resumeUrl) {
          setHeading('Magic link approved');
          setMessage('Magic link approved. Finishing sign in...');
          const completed = await onApproved(result.resumeUrl);
          if (!completed && mountedRef.current) {
            setMessage(
              'Your magic link was approved, but finishing sign in failed. Use the retry button above.',
            );
          }
          return;
        }
        setTerminalState(approvedElsewhereState());
      } catch (error) {
        if (error instanceof InteractionApiError) {
          if (mountedRef.current) {
            onTerminal(error);
          }
          return;
        }
        reportInteractionFailure(error, {
          source: 'magic-link-consume-state',
          endpoint: '/api/auth/interactions/magic_link/consume',
          operation: 'consume',
        });
        if (!mountedRef.current) {
          return;
        }
        setTerminalState({
          title: 'Magic link failed',
          message: 'Please start over and try again.',
          actionLabel: 'Start over',
        });
      }
    }

    consume();

    return () => {
      mountedRef.current = false;
    };
  }, [interactionToken, magicLinkToken, onApproved, onTerminal, sameDevice]);

  if (terminalState) {
    return <TerminalInteractionStateView state={terminalState} onRestart={onRestart} />;
  }

  return (
    <div className="w-full max-w-lg">
      <div className="mb-6">
        <h2 className="h3 font-merriweather mb-2">{heading}</h2>
        <p className="text-sm text-slate-600">{message}</p>
      </div>
    </div>
  );
}
